// hooks/useListView.ts — a list's View choice (cards / table, map / table…) for the Filter card's "View" group.
// The URL wins (?view=, so a link lands on the view it names); without it the choice remembered in this browser
// ('<list>.view'), else the default. ?view= is written only when the choice is not the default.
import { useEffect, useState } from 'react';

const read = (key: string): string | null => { try { return localStorage.getItem(key); } catch { return null; } };
const write = (key: string, v: string) => { try { localStorage.setItem(key, v); } catch { /* private mode */ } };

/**
 * Returns [view, setView, remember]. `setView` remembers the choice and writes ?view=. `remember` only remembers it and
 * returns the ?view= value (null for the default) for a caller that writes several keys in one patch — two separate
 * search-param writes in the same tick would lose the first.
 */
export function useListView<V extends string>(storageKey: string, views: readonly V[], fallback: V, fromUrl: string | null, setUrl: (v: V | null) => void): [V, (v: V) => void, (v: V) => V | null] {
  // Re-read storage after a choice even when the URL does not change (the default chosen with no ?view= to drop).
  const [, bump] = useState(0);
  const isView = (v: string | null): v is V => v !== null && (views as readonly string[]).includes(v);
  const stored = read(storageKey);
  const view: V = isView(fromUrl) ? fromUrl : isView(stored) ? stored : fallback;
  useEffect(() => { write(storageKey, view); }, [storageKey, view]);
  const remember = (v: V): V | null => {
    // Remember it first: without ?view= the list reads the remembered choice, so the default must be stored before the param goes.
    write(storageKey, v);
    bump((n) => n + 1);
    return v === fallback ? null : v;
  };
  const setView = (v: V) => setUrl(remember(v));
  return [view, setView, remember];
}
